import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Label,
  Input,
} from "reactstrap";
import moment from "moment";
import Chart from "react-google-charts";
import ReactApexChart from "react-apexcharts";
import { toast } from "react-toastify";
import { Image } from "../../../AbstractElements";
import { dynamicImage } from "../../../Utils";
import { getData, postData } from "../../../CommonElements/Functions/PostGetFun";
import { API } from "../../../api/API";
import { isOwnRequest } from "../../../Utils/authUtils";
import "../Styles/userMtp.css";

interface UserMtpProps {
  employeeIdProp: number;
  positionId?: number;
}

interface OutletType {
  Count?: number;
  count?: number;
  OutlettypeName: string;
  Coverage: number;
}

interface AgendaDay {
  Count: number;
  Agendacontroltype: string;
}

interface MtpRowItem {
  MtpId: number;
  Month: string; // e.g. "09-2026" or "2026-09"
  MtpStatus: string; // "approved", "requested", "draft", "rejected"
  employee?: string;
  EmployeeName?: string;
  employee_id?: number;
  EmployeeId?: number;
  position_id?: number;
  PositionId?: number;
  TotalOutlets?: OutletType[];
  AgendaDays?: AgendaDay[];
  TotalVisits?: number;
  VisitsFq?: number;
  TotalLeaves?: number;
  RemainingDoctor?: number;
  RemainingOutlet?: string;
  ApprovedDate?: string;
  createdAt?: string;
  CreatedAt?: string;
  UpdatedAt?: string;
}

const RadialBarChart: React.FC<{
  value: number;
  total: number;
  label: string;
  color?: string;
  size?: number;
}> = ({ value, total, label, color = "#00c8ff", size = 145 }) => {
  const percentage = total > 0 ? Math.min(100, Math.round((value / total) * 100)) : 0;

  const options: ApexCharts.ApexOptions = {
    series: [percentage],
    chart: {
      type: "radialBar",
      sparkline: { enabled: true },
    },
    plotOptions: {
      radialBar: {
        hollow: { size: "50%" },
        track: {
          background: "#e8edf6",
          strokeWidth: "100%",
        },
        dataLabels: {
          name: { show: false },
          value: {
            show: true,
            fontSize: "13px",
            fontWeight: "700",
            color: "#1c65d3",
            offsetY: 5,
            formatter: () => `${value}/${total}`,
          },
        },
      },
    },
    colors: [color],
    stroke: {
      lineCap: "round",
    },
  };

  return (
    <div className="mtp-indicator-box">
      <div className="mtp-chart-svg-wrap">
        <ReactApexChart options={options} series={[percentage]} type="radialBar" height={size} width={size} />
      </div>
      <div className="mtp-indicator-label" title={label}>
        {label}
      </div>
    </div>
  );
};

const HalfGaugeChart: React.FC<{
  visits: number;
  visitsFq: number;
  size?: number;
}> = ({ visits, visitsFq, size = 145 }) => {
  const percentage = visitsFq > 0 ? Math.min(100, Math.round((visits / visitsFq) * 100)) : 0;

  const options: ApexCharts.ApexOptions = {
    series: [percentage],
    chart: {
      type: "radialBar",
      offsetY: 0,
      sparkline: { enabled: true },
    },
    plotOptions: {
      radialBar: {
        startAngle: -90,
        endAngle: 90,
        hollow: { size: "50%" },
        track: {
          background: "#e8edf6",
          strokeWidth: "100%",
        },
        dataLabels: {
          name: { show: false },
          value: {
            show: true,
            fontSize: "13px",
            fontWeight: "700",
            color: "#1e293b",
            offsetY: -6,
            formatter: () => `${visits}/ ${visitsFq}`,
          },
        },
      },
    },
    fill: { colors: ["#d9534f"] },
    stroke: { lineCap: "round" },
  };

  return (
    <div className="mtp-indicator-box">
      <div className="mtp-chart-svg-wrap">
        <ReactApexChart options={options} series={[percentage]} type="radialBar" height={size} width={size} />
      </div>
      <div className="mtp-indicator-label" style={{ color: "#d9534f", fontWeight: 700 }}>
        Visit FQ
      </div>
    </div>
  );
};

const AgendaDonutChart: React.FC<{
  agendaDays?: AgendaDay[];
  totalLeaves?: number;
  size?: number;
}> = ({ agendaDays = [], totalLeaves = 0, size = 145 }) => {
  const ncaCount = agendaDays.find((a) => a.Agendacontroltype?.toUpperCase() === "NCA")?.Count || 0;
  const fwCount = agendaDays.find((a) => a.Agendacontroltype?.toUpperCase() === "FW")?.Count || 0;

  const chartData = [
    ["Agenda", "Days"],
    ["NCA", ncaCount],
    ["FW", fwCount],
    ["Leave", totalLeaves],
  ];

  return (
    <div className="mtp-indicator-box">
      <div className="mtp-chart-svg-wrap" style={{ height: `${size}px`, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Chart
          chartType="PieChart"
          width={`${size}px`}
          height={`${size - 15}px`}
          data={chartData}
          options={{
            pieHole: 0.70,
            legend: { position: "none" },
            pieSliceText: "none",
            slices: {
              0: { color: "#007FF3" }, // NCA
              1: { color: "#0094D6" }, // FW
              2: { color: "#33BDFA" }, // Leave
            },
            chartArea: { width: "82%", height: "82%" },
            backgroundColor: "transparent",
          }}
        />
      </div>
      <div className="mtp-indicator-label" style={{ fontSize: "11px", whiteSpace: "nowrap" }}>
        <span style={{ color: "#007FF3" }}>NCA({ncaCount})</span> |{" "}
        <span style={{ color: "#0094D6" }}>FW({fwCount})</span> |{" "}
        <span style={{ color: "#33BDFA" }}>Leave({totalLeaves})</span>
      </div>
    </div>
  );
};


const UserMtp: React.FC<UserMtpProps> = ({ employeeIdProp, positionId }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const serverUrl = localStorage.getItem("serverUrl") || "";

  const [loading, setLoading] = useState<boolean>(true);
  const [creating, setCreating] = useState<boolean>(false);
  const [mtpRows, setMtpRows] = useState<MtpRowItem[]>([]);

  // Approval Modal State
  const [confirmModalOpen, setConfirmModalOpen] = useState<boolean>(false);
  const [selectedItemForAction, setSelectedItemForAction] = useState<MtpRowItem | null>(null);
  const [confirmActionType, setConfirmActionType] = useState<"approve" | "reject">("approve");
  const [isStp, setIsStp] = useState<boolean>(false);
  const [actionLoading, setActionLoading] = useState<boolean>(false);

  const fetchMtpData = async () => {
    setLoading(true);
    try {
      let serverBaseUrl = serverUrl;
      if (serverBaseUrl && !serverBaseUrl.endsWith("/")) {
        serverBaseUrl += "/";
      }

      let list: MtpRowItem[] = [];

      // Primary API: getMTPListV2 (api/plans/mtp/v2)
      const queryParams: Record<string, any> = {};
      if (positionId) queryParams.position_id = positionId;
      if (employeeIdProp) queryParams.employee_id = employeeIdProp;

      const urlV2 = `${serverBaseUrl}${API.getMTPListV2}`;
      const resV2 = await getData(urlV2, true, queryParams);

      if (resV2?.status === 200) {
        if (Array.isArray(resV2?.data?.data?.mtp)) {
          list = resV2.data.data.mtp;
        } else if (Array.isArray(resV2?.data?.mtp)) {
          list = resV2.data.mtp;
        } else if (Array.isArray(resV2?.data?.data)) {
          list = resV2.data.data;
        } else if (Array.isArray(resV2?.data)) {
          list = resV2.data;
        }
      }

      // Filter list to strictly show only this user's own record if multiple are returned
      if (list.length > 0 && (employeeIdProp || positionId)) {
        const userSpecific = list.filter((item: any) => {
          if (
            employeeIdProp &&
            (String(item.employee_id) === String(employeeIdProp) ||
              String(item.EmployeeId) === String(employeeIdProp))
          ) {
            return true;
          }
          if (
            positionId &&
            (String(item.position_id) === String(positionId) ||
              String(item.PositionId) === String(positionId))
          ) {
            return true;
          }
          return false;
        });

        if (userSpecific.length > 0) {
          list = userSpecific;
        }
      }

      setMtpRows(list);
    } catch (err) {
      console.error("Error fetching MTP data in UserMtp:", err);
      setMtpRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMtpData();
  }, [employeeIdProp, positionId]);

  const createMtpRow = async () => {
    setCreating(true);
    try {
      let serverBaseUrl = serverUrl;
      if (serverBaseUrl && !serverBaseUrl.endsWith("/")) {
        serverBaseUrl += "/";
      }

      const nextMonthMoment = moment().add(1, "month");
      const month = nextMonthMoment.format("MM");
      const year = nextMonthMoment.format("YYYY");

      const url = `${serverBaseUrl}${API.createMTP}`;
      const response = await postData(url, true, { month, year });

      const resObj = response as any;
      if (response.status === 200 || resObj?.statusCode === 200) {
        toast.success("Month MTP Created Successfully!");
        fetchMtpData();
      } else {
        toast.error(resObj?.message || response.data?.message || "Failed to create MTP month");
      }
    } catch (err) {
      console.error("Error in createMtpRow:", err);
      toast.error("An error occurred while creating MTP");
    } finally {
      setCreating(false);
    }
  };

  const handleActionSubmit = async () => {
    if (!selectedItemForAction) return;
    setActionLoading(true);
    try {
      let serverBaseUrl = serverUrl;
      if (serverBaseUrl && !serverBaseUrl.endsWith("/")) {
        serverBaseUrl += "/";
      }

      const targetUrl = `${serverBaseUrl}${
        confirmActionType === "approve" ? API.approveMTP : API.rejectMTP
      }`;

      const params: any = { mtp_id: selectedItemForAction.MtpId };
      if (isStp) {
        params.is_stp = true;
      }

      const res = await getData(targetUrl, true, params);

      if (res.status === 200) {
        toast.success(res.data?.message || `MTP ${confirmActionType === "approve" ? "Approved" : "Rejected"} Successfully!`);
        fetchMtpData();
      } else {
        toast.error(res.data?.message || `Failed to ${confirmActionType} MTP.`);
      }
    } catch (err) {
      console.error("Error executing MTP action:", err);
      toast.error("An error occurred executing MTP action.");
    } finally {
      setActionLoading(false);
      setConfirmModalOpen(false);
      setSelectedItemForAction(null);
    }
  };

  const getStatusBadge = (status?: string) => {
    const st = (status || "").toLowerCase();
    switch (st) {
      case "approved":
        return <span className="mtp-status-pill approved">Approved</span>;
      case "requested":
        return <span className="mtp-status-pill requested">Requested</span>;
      case "draft":
        return <span className="mtp-status-pill draft">Draft</span>;
      case "rejected":
      case "reject":
        return <span className="mtp-status-pill rejected">Rejected</span>;
      default:
        return <span className="mtp-status-pill draft">{status || "N/A"}</span>;
    }
  };

  const formatMonthTitle = (monthStr?: string) => {
    if (!monthStr) return "";
    let formattedDate = "";
    if (monthStr.includes("-")) {
      const parts = monthStr.split("-");
      if (parts[0].length === 4) {
        // YYYY-MM
        formattedDate = `${parts[0]}-${parts[1]}-01`;
      } else {
        // MM-YYYY
        formattedDate = `${parts[1]}-${parts[0]}-01`;
      }
    } else {
      formattedDate = monthStr;
    }
    return moment(formattedDate).isValid() ? moment(formattedDate).format("MMMM YYYY") : monthStr;
  };

  const formatMonthParam = (monthStr?: string) => {
    if (!monthStr) return moment().format("YYYY-MM");
    if (monthStr.includes("-")) {
      const parts = monthStr.split("-");
      if (parts[0].length === 4) return monthStr; // already YYYY-MM
      return `${parts[1]}-${parts[0]}`; // MM-YYYY to YYYY-MM
    }
    return monthStr;
  };

  const handleViewClick = (item: MtpRowItem) => {
    navigate("/mtpcalendarbig", {
      state: {
        mtpStatus: item.MtpStatus,
        updated: formatMonthParam(item.Month),
        mtpId: item.MtpId || (item as any).mtp_id || (item as any).id,
        itemData: item,
        backPath: location.pathname,
        backState: {
          employeeId: employeeIdProp,
          positionId: positionId,
          activeTab: "7",
        },
      },
    });
  };

  return (
    <div className="user-mtp-container">
      {/* Header & Create Button */}
      <div className="user-mtp-header">
        <h5 className="user-mtp-title">
          <span className="user-mtp-title-icon">
            <i className="icofont icofont-calendar"></i>
          </span>
          MTP Summary
        </h5>
        <div className="d-flex align-items-center">
          <button
            type="button"
            className="btn-create-mtp"
            disabled={creating}
            onClick={createMtpRow}
          >
            <i className="icofont icofont-plus" />
            {creating ? "Creating..." : "Create new MTP"}
          </button>
        </div>
      </div>

      {/* Main Content */}
      {loading ? (
        <div className="text-center py-5 w-100">
          <Image
            className="b-r-10 img-80"
            src={dynamicImage("dashboard/loader.gif")}
            alt="Loading..."
          />
        </div>
      ) : mtpRows.length === 0 ? (
        <div className="mtp-empty-card w-100">
          <div className="text-muted mb-2" style={{ fontSize: "16px", fontWeight: 600 }}>
            <i className="icofont icofont-info-circle me-1" /> No MTP Found
          </div>
          <p className="text-muted small mb-0">
            There are no MTP records found. Click &quot;Create new MTP&quot; above to generate a new month MTP.
          </p>
        </div>
      ) : (
        <div className="w-100 d-flex flex-column gap-3">
          {mtpRows.map((item, index) => {
            const empName = item.employee || item.EmployeeName || "";
            const monthDisplay = formatMonthTitle(item.Month);
            const titleText = empName ? `${empName} / ${monthDisplay}` : monthDisplay;
            const dateDisplay = item.ApprovedDate || item.createdAt || item.CreatedAt || item.UpdatedAt;

            // Compute Missed Outlet Count
            const matchedOutlet = item.TotalOutlets?.find(
              (o) => o.OutlettypeName?.toLowerCase() === item.RemainingOutlet?.toLowerCase()
            );
            const totalOutletCount = matchedOutlet
              ? matchedOutlet.count !== undefined
                ? matchedOutlet.count
                : matchedOutlet.Count || 0
              : 0;

            const isOwn = isOwnRequest(item) || (employeeIdProp && String(employeeIdProp) === (localStorage.getItem("empId") || localStorage.getItem("employeeId"))) || (positionId && String(positionId) === localStorage.getItem("positionId"));
            const isRequested = item.MtpStatus?.toLowerCase() === "requested";

            return (
              <div key={index} className="mtp-month-card w-100">
                {/* Card Header */}
                <div className="mtp-card-header">
                  <div className="mtp-card-header-left">
                    <span className="mtp-month-title">
                      {titleText}
                    </span>
                    {getStatusBadge(item.MtpStatus)}
                    {dateDisplay && (
                      <span className="mtp-date-badge">
                        On {moment(dateDisplay).isValid() ? moment(dateDisplay).format("DD-MM-YYYY") : dateDisplay}
                      </span>
                    )}
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    {isRequested && !isOwn && (
                      <>
                        <Button
                          color="success"
                          size="sm"
                          onClick={() => {
                            setSelectedItemForAction(item);
                            setConfirmActionType("approve");
                            setConfirmModalOpen(true);
                          }}
                        >
                          Approve
                        </Button>
                        <Button
                          color="danger"
                          size="sm"
                          onClick={() => {
                            setSelectedItemForAction(item);
                            setConfirmActionType("reject");
                            setConfirmModalOpen(true);
                          }}
                        >
                          Reject
                        </Button>
                      </>
                    )}
                    <button
                      type="button"
                      className="btn-view-calendar-3d"
                      onClick={() => handleViewClick(item)}
                    >
                      <i className="icofont icofont-calendar" />
                      View Plan
                    </button>
                  </div>
                </div>

                {/* Card Charts Body - Single Horizontal Row matching reference screenshot */}
                <div className="mtp-single-row-scroll">
                  {/* Outlets Coverage Radial Bars */}
                  {item.TotalOutlets && item.TotalOutlets.length > 0 ? (
                    item.TotalOutlets.map((outlet, oIdx) => {
                      const countVal = outlet.count !== undefined ? outlet.count : outlet.Count || 0;
                      return (
                        <div key={oIdx} className="mtp-circle-item">
                          <RadialBarChart
                            value={outlet.Coverage || 0}
                            total={countVal}
                            label={`${outlet.OutlettypeName || "Outlet"} Coverage`}
                            color="#00c8ff"
                            size={145}
                          />
                        </div>
                      );
                    })
                  ) : (
                    <div className="mtp-circle-item">
                      <RadialBarChart
                        value={0}
                        total={0}
                        label="Doctor Coverage"
                        color="#00c8ff"
                        size={145}
                      />
                    </div>
                  )}

                  {/* Visit Frequency Chart */}
                  <div className="mtp-circle-item">
                    <HalfGaugeChart
                      visits={item.TotalVisits || 0}
                      visitsFq={item.VisitsFq || 0}
                      size={145}
                    />
                  </div>

                  {/* Agenda Distribution Donut */}
                  <div className="mtp-circle-item">
                    <AgendaDonutChart
                      agendaDays={item.AgendaDays}
                      totalLeaves={item.TotalLeaves || 0}
                      size={145}
                    />
                  </div>

                  {/* Missed Doctors Radial Bar */}
                  <div className="mtp-circle-item">
                    <RadialBarChart
                      value={item.RemainingDoctor || 0}
                      total={totalOutletCount}
                      label={`Missed ${item.RemainingOutlet || "Doctors"}`}
                      color="#f39c12"
                      size={145}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Confirmation Modal for Approve / Reject */}
      <Modal isOpen={confirmModalOpen} toggle={() => setConfirmModalOpen(!confirmModalOpen)}>
        <ModalHeader toggle={() => setConfirmModalOpen(!confirmModalOpen)}>
          {confirmActionType === "approve" ? "Approve MTP" : "Reject MTP"}
        </ModalHeader>
        <ModalBody>
          <p>Are you sure you want to {confirmActionType} this MTP?</p>
          <FormGroup check className="mt-3">
            <Label check>
              <Input
                type="checkbox"
                checked={isStp}
                onChange={(e) => setIsStp(e.target.checked)}
              />{" "}
              I want to submit this MTP as my standard tour plan (STP)
            </Label>
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button
            color={confirmActionType === "approve" ? "success" : "danger"}
            disabled={actionLoading}
            onClick={handleActionSubmit}
          >
            {actionLoading ? "Processing..." : `Yes, ${confirmActionType === "approve" ? "Approve" : "Reject"}`}
          </Button>
          <Button color="secondary" onClick={() => setConfirmModalOpen(false)}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default UserMtp;
